import SessionService from './sessionService';
import { decodeAccessToken, isAccessTokenExpired } from '../utils/accessToken';

export const ACCESS_ROLES = {
    ANONYMOUS: 'anonymous',
    FREE: 'free',
    PRO: 'pro',
    ADMIN: 'admin'
};

export const ACCESS_FEATURES = {
    API_KEYS: 'api',
    ADMIN_TOOLS: 'admin.tools',
    PARLAY_GENERATOR: 'parlay.generator',
    PARLAY_OPTIONS: 'parlay.options',
    PICKS_FILTERS: 'picks.filters',
    SNIPED_ODDS_FILTERS: 'sniped_odds.filters'
};

const VALID_TIERS = new Set([
    ACCESS_ROLES.FREE,
    ACCESS_ROLES.PRO,
    ACCESS_ROLES.ADMIN
]);

export const normalizeTier = (tier) => {
    const normalized = String(tier || '').toLowerCase();
    return VALID_TIERS.has(normalized) ? normalized : ACCESS_ROLES.FREE;
};

const getTierFromSessionState = (sessionService) => {
    const subscriptionState = sessionService.getPaymentSubscription();
    const subscriptionTier = String(subscriptionState?.currentTier || '').toLowerCase();

    if (subscriptionState?.isPaid && subscriptionTier === ACCESS_ROLES.PRO) {
        return ACCESS_ROLES.PRO;
    }

    const settingsTier = String(
        sessionService.getUserSettings()?.tier?.currentTier ||
        sessionService.getUserSettings()?.tier?.name ||
        sessionService.getUserSettings()?.currentTier ||
        sessionService.getUserSettings()?.tier ||
        ''
    ).toLowerCase();

    return VALID_TIERS.has(settingsTier) ? settingsTier : '';
};

class AccessService {
    static _instance = null;

    constructor() {
        if (AccessService._instance) {
            return AccessService._instance;
        }

        AccessService._instance = this;
    }

    static getInstance() {
        return AccessService._instance || (AccessService._instance = new AccessService());
    }

    getClaims() {
        return decodeAccessToken(SessionService.getInstance().getAccessToken());
    }

    getAccess() {
        const sessionService = SessionService.getInstance();
        const claims = this.getClaims();
        const isAuthenticated = Boolean(
            sessionService.getSessionId() &&
            sessionService.getAccessToken() &&
            claims?.sub &&
            !isAccessTokenExpired(claims)
        );

        if (!isAuthenticated) {
            return {
                isAuthenticated: false,
                userId: null,
                sessionId: null,
                tier: ACCESS_ROLES.ANONYMOUS,
                isAdmin: false,
                claims
            };
        }

        const tokenTier = normalizeTier(claims.tier);
        const sessionTier = getTierFromSessionState(sessionService);
        const tier = tokenTier === ACCESS_ROLES.ADMIN
            ? ACCESS_ROLES.ADMIN
            : normalizeTier(sessionTier || tokenTier);

        return {
            isAuthenticated: true,
            userId: claims.sub,
            sessionId: claims.sid || sessionService.getSessionId(),
            tier,
            isAdmin: tier === ACCESS_ROLES.ADMIN,
            claims
        };
    }

    isAuthenticated() {
        return this.getAccess().isAuthenticated;
    }

    isAnonymous() {
        return !this.isAuthenticated();
    }

    isProOrAdmin() {
        const { tier } = this.getAccess();
        return tier === ACCESS_ROLES.PRO || tier === ACCESS_ROLES.ADMIN;
    }

    isAdmin() {
        return this.getAccess().tier === ACCESS_ROLES.ADMIN;
    }
}

export default AccessService;
